import * as Tone from 'tone';
import { BarsBeatsSixteenths } from 'tone/build/esm/core/type/Units';
import { onMounted, onUnmounted, Ref, ref, reactive, computed, ComputedRef, watch } from 'vue';
import { getUrl } from '@/utils/url';

const MUSIC_DURATION = 90;
Tone.Transport.loop = true;
Tone.Transport.loopStart = 0;
Tone.Transport.loopEnd = MUSIC_DURATION;

const DEFAULT_AUDIO_DURATIOM = 13740;
const DEFAULT_AUDIO_DURATIOM_B = 1000;
type UseSynthType = {
    noteRef: Ref<string>;
    run: (newNote?: string) => number;
    interId?: number;
    cancel: (note?: string | undefined) => void;
    playing: ComputedRef<boolean>
}

type SamplesMapType = {[key: string]: string};
const samplesMap: {[key: string]: string} = {};
export type synthMap<T> = {
    A: Array<T>,
    B: Array<T>,
    C: Array<T>,
    D: Array<T>,
}

export const audioMap: synthMap<number> = {
    A: [49, 50, 51, 52, 53, 54, 55, 56], // 1 ~ 8, 57, 48,
    B: [81, 87, 69, 82, 84, 89, 85, 73, 79], // q~o
    C: [65, 83, 68, 70, 71, 72, 74, 75], // a~k
    D: [90, 88, 67, 86, 66, 78, 77, 188, 190], // z~m
};

export type GrpType = 'A' | 'B' | 'C' | 'D';

export const keyCode2GrpMap: { [key: string]: GrpType } = {};
Object.entries(audioMap).forEach(([key, value]) => {
    value.forEach((item) => {
        keyCode2GrpMap[item] = key as GrpType;
    });
});

export type SynthInfo = {k: number, n: string, e: 0 | 1, s: 0 | 1, pk?: string, fd?: 0 | 1 }

export const defaultKeyMap: synthMap<SynthInfo> = {
    A: [{ k: 49, n: '1', e: 0, s: 0 }, { k: 50, n: '2', e: 0, s: 0 }, { k: 51, n: '3', e: 0, s: 0 }],
    B: [{ k: 81, n: 'Q', e: 0, s: 0 }, { k: 87, n: 'W', e: 0, s: 0 }, { k: 69, n: 'E', e: 0, s: 0 }],
    C: [{ k: 65, n: 'A', e: 0, s: 0 }, { k: 83, n: 'S', e: 0, s: 0 }, { k: 68, n: 'D', e: 0, s: 0 }],
    D: [{ k: 90, n: 'Z', e: 0, s: 0 }, { k: 88, n: 'X', e: 0, s: 0 }, { k: 67, n: 'C', e: 0, s: 0 }],
};

function parseDefaultSampler(keyMap: synthMap<SynthInfo>) {
    const map: SamplesMapType = {};
    Object.entries(keyMap).forEach(([key, group]) => {
        group.forEach((item, idx) => {
            if (item.fd) return;
            group[idx].e = 1;
            const note = `${key.toLowerCase()}${idx}`;
            // eslint-disable-next-line default-case
            switch (key) {
                case 'A':
                    map[note] = getUrl(`/demi/${note}.mp3`);
                    break;
                case 'B':
                    map[note] = getUrl(`/demi/${note}.mp3`);
                    break;
                case 'C':
                    map[note] = getUrl(`/demi/${note}.mp3`);
                    break;
                case 'D':
                    map[note] = getUrl(`/demi/${note}.mp3`);
                    break;
            }
        });
    });

    return map;
}
let samplerSynth: Tone.Sampler | null = null;
export function useDemi(onPlay = (key: string, idx: number, keyMap: synthMap<SynthInfo>) => {}) {
    const isLoaded = ref(false);
    const keyMap = reactive(defaultKeyMap);

    const init = () => {
        isLoaded.value = false;
        const samplerSynthsMap = parseDefaultSampler(keyMap);
        samplerSynth = new Tone.Sampler({
            urls: samplerSynthsMap,
            release: 0,
            baseUrl: '',
            onload: () => {
                isLoaded.value = true;
            },
        }).toDestination();
    };

    const { playing: mP, cancel: mainCancel, run: mRun } = usePlayer(getUrl('/demi/space.mp3'));
    const { playing: aP, cancel: saCancel, run: saRun } = useSynth(3000);
    const { playing: bP, cancel: sbCancel, run: sbRun } = useSynth(2000);
    const { playing: cP, cancel: scCancel, run: scRun } = useSynth(3000);
    const { playing: dP, cancel: sdCancel, run: sdRun } = useSynth(5000);
    const isPlaying = computed(() => mP.value || aP.value
        || bP.value
        || cP.value
        || dP.value);
    const currentNote = reactive({
        A: [] as Array<number>,
        B: [] as Array<number>,
        C: [] as Array<number>,
        D: [] as Array<number>,
    });
    const clearCurrentNote = () => {
        currentNote.A = [];
        currentNote.B = [];
        currentNote.C = [];
        currentNote.D = [];
    };

    const toggleStart = () => {
        Tone.start();
        setTimeout(() => {
            if (!mP.value) {
                mRun();
            } else {
                mainCancel();
            }
        }, 100);
        return isPlaying.value;
    };

    const play = (key: string, idx: number, keyCode: number) => {
        const note = `${key}${idx}`;
        onPlay(key, idx, keyMap);
        // eslint-disable-next-line default-case
        switch (key) {
            case 'A':
                currentNote.A.push(keyCode);
                setTimeout(() => {
                    currentNote.A.shift();
                }, 3000);
                saRun(note);
                break;
            case 'B':
                currentNote.B.push(keyCode);
                setTimeout(() => {
                    currentNote.B.shift();
                }, 2000);
                sbRun(note);
                break;
            case 'C':
                currentNote.C.push(keyCode);
                setTimeout(() => {
                    currentNote.C.shift();
                }, 3000);
                scRun(note);
                break;
            case 'D':
                currentNote.D.push(keyCode);
                setTimeout(() => {
                    currentNote.D.shift();
                }, 5000);
                sdRun(note);
                break;
        }
    };

    const stopAll = () => {
        saCancel();
        sbCancel();
        scCancel();
        sdCancel();
        mainCancel();
        clearCurrentNote();
        samplerSynth?.releaseAll();
    };

    //

    onMounted(() => {
        window.addEventListener('keydown', keypressHandler);
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', keypressHandler);
        stopAll();
    });

    function keypressHandler(event: KeyboardEvent) {
        const keyCode = event?.keyCode;
        if (keyCode === 32) {
            event.preventDefault();
            if (isPlaying.value) {
                stopAll();
            } else {
                toggleStart();
            }
            return;
        }
        // if (keyCode === 13) {
        //   attack();
        //   return;
        // }
        if ((keyCode >= 49 && keyCode <= 177) || (keyCode === 188 || keyCode === 190)) {
            const key = keyCode2GrpMap[keyCode];
            const index = audioMap[key].indexOf(keyCode);
            if (index >= 0) {
                const km = keyMap[key];
                if (!km[index]?.e) return;
                play(key, index, keyCode);
            }
        }
    }
    return {
        init,
        play,
        stopAll,
        // keypressHandler,
        toggleStart,
        currentNote,
        isLoaded,
        isPlaying,
        keyMap,
    };
}

function useSynthLoop(time = DEFAULT_AUDIO_DURATIOM): UseSynthType {
    const timeoutId = ref(0);
    const interId = ref(0);
    const noteRef = ref('');
    const run = (newNote = '') => {
        cancel(noteRef.value);
        noteRef.value = newNote || noteRef.value;
        if (!noteRef.value) return 0;
    samplerSynth?.triggerAttack(noteRef.value);
    const id = setTimeout(() => {
      samplerSynth?.triggerRelease(noteRef.value);
    }, time) as unknown as number;
    interId.value = setInterval(() => {
        run();
    }, time) as unknown as number;
    timeoutId.value = id;
    return timeoutId.value;
    };

    function cancel(note: string | undefined = '') {
        if (!samplerSynth) return;

        if (note) {
            samplerSynth.triggerRelease(note);
        } else {
            samplerSynth.triggerRelease(noteRef.value);
            noteRef.value = '';
        }
        clearInterval(interId.value);
        interId.value = 0;
        clearTimeout(timeoutId.value);
        timeoutId.value = 0;
    }

    const playing = computed(() => timeoutId.value > 0);

    return {
        noteRef,
        run,
        interId: interId as unknown as number,
        cancel,
        playing,
    };
}

function usePlayer(url: string) {
    const transportState: {
        intervalId: number,
        position: BarsBeatsSixteenths,
    } = {
        intervalId: 0,
        position: '0:0:0',
    };
    const isLoaded = ref(false);
    const playing = ref(false);
    const player = new Tone.Player({
        url,
        loop: true,
        volume: 0.8,
        mute: false,
        onload() {
            isLoaded.value = true;
        },
    }).toDestination().sync().start(0);

    const run = () => {
        if (!playing.value) {
            playing.value = true;
            clearInterval(transportState.intervalId);
            Tone.Transport.start('+0', transportState.position);
            transportState.intervalId = setInterval(() => {
                transportState.position = Tone.Transport.position as BarsBeatsSixteenths;
            }, 16) as unknown as number;
        }
    };

    const cancel = () => {
        if (playing.value) {
            playing.value = false;
            clearInterval(transportState.intervalId);
            // player.stop();
            Tone.Transport.stop();
        }
    };

    return {
        run,
        cancel,
        playing,
    };
}

function useSynth(time = DEFAULT_AUDIO_DURATIOM_B, isOverlap = false): UseSynthType {
    const noteRef = ref('');
    const timeoutId = reactive<{ [key: string]: number}>({});
    const run = (newNote = '') => {
        if (!isOverlap && newNote && noteRef.value === newNote && timeoutId[newNote] > 0) cancel(noteRef.value);
        noteRef.value = newNote || noteRef.value;
        if (!noteRef.value) return 0;
    samplerSynth?.triggerAttack(noteRef.value);
    const id = setTimeout(() => {
        // samplerSynth?.triggerRelease(newNote);
        cancel(newNote);
    }, time) as unknown as number;
    timeoutId[noteRef.value] = id;
    return timeoutId[noteRef.value];
    };

    function cancel(note: string | undefined) {
        if (note) {
      samplerSynth?.triggerRelease(note);
      clearTimeout(timeoutId[note]);
      timeoutId[note] = 0;
        } else {
      samplerSynth?.triggerRelease(noteRef.value);
      clearTimeout(timeoutId[noteRef.value]);
      timeoutId[noteRef.value] = 0;
      noteRef.value = '';
        }
    }

    const playing = computed(() => !!Object.values(timeoutId).find(v => v > 0));
    return {
        noteRef,
        run,
        cancel,
        playing,
    };
}
